import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { formatDay } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Erasing a customer at the counter. §6.2 `CUS-006`, §15.10, Appendix M `RET-01`, release gate 10.
 *
 * **`RET-01` is the whole shape of this screen:** "Erasure computes what can and cannot go, shows
 * the customer exactly what is retained and why, and executes the permitted portion. Never
 * all-or-nothing." So the plan is read and displayed before anything is offered to press, the two
 * lists are shown side by side rather than one summarised, and the reason for every retained line
 * is in the customer's words because they are the person being told.
 *
 * **The confirmation sends `acknowledgedRetention`, which the server requires.** That is what makes
 * the showing a condition of the write rather than a habit of this screen: a client that posts
 * without it is refused, so there is no path to an erasure nobody was shown.
 *
 * **A blocked request is not an error here either.** §15.10: "the request is recorded and queued,
 * the customer is told why it cannot complete yet, and it executes automatically once the blocking
 * condition clears." The screen says what is in the way and that the request is on file from today,
 * which is the date that matters. What it does not say is that the shop need do nothing: the
 * automatic half needs a worker that does not exist, so the honest sentence is that the shop
 * finishes it once the work is collected or the money is in.
 *
 * **It never offers to erase while the plan is still loading**, because the one thing this screen
 * must not do is take a decision on a customer's data from a button somebody pressed before they
 * could read anything.
 */

/** One line of the plan, as `planErasure` builds it. */
export interface ErasureItemData {
  readonly kind: string
  readonly id: string
  readonly label: string
  readonly retainedBecause?: string
}

export interface ErasurePlanData {
  readonly toErase: readonly ErasureItemData[]
  readonly toRetain: readonly ErasureItemData[]
  /** One line per distinct reason, in the customer's words. `RET-01`'s "and why". */
  readonly retentionNotice: readonly string[]
  readonly isPartial: boolean
}

export interface ErasurePlanAnswer {
  readonly plan: ErasurePlanData
  readonly blockedBecause: readonly string[]
  readonly alreadyErasedAt: string | null
  readonly queuedSince: string | null
}

export interface CustomerErasureProps {
  readonly customerId: string
  readonly customerName: string
  readonly loadPlan: (customerId: string) => Promise<ErasurePlanAnswer>
  /** Appendix B's `customer_erase`. The route checks the same row. */
  readonly canErase: boolean
  /**
   * The branch's timezone, for `LOC-004`.
   *
   * §15.10 says the date the request was made is what counts, and this screen printed it as the
   * raw ISO day the server sent. The one date on the panel that carries a legal consequence is
   * the one a shopkeeper reads back to the customer, so it is written the way they write one.
   */
  readonly timezone: string
  readonly onErased?: () => void
}

export function CustomerErasure({
  customerId,
  customerName,
  loadPlan,
  canErase,
  timezone,
  onErased,
}: CustomerErasureProps): JSX.Element | null {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [open, setOpen] = useState(false)
  const [answer, setAnswer] = useState<ErasurePlanAnswer | null>(null)
  const [outcome, setOutcome] = useState<{
    readonly status: string
    readonly blockedBecause: readonly string[]
  } | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  /** Only the plan read can fail on this panel; the confirm is a queued write with no message. */
  const [loadError, setLoadError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    void loadPlan(customerId)
      .then((loaded) => {
        if (cancelled) return
        setAnswer(loaded)
        setLoadError(null)
      })
      .catch(() => {
        // Refused rather than degraded. An erasure screen that silently showed an empty plan would
        // invite somebody to confirm the erasure of a customer whose records it could not read.
        if (!cancelled) setLoadError(t('erasure.planFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [customerId, loadPlan, open, t, attempt])

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    const body = (
      typeof write.response === 'object' && write.response !== null ? write.response : {}
    ) as { status?: unknown; blockedBecause?: unknown }
    setOutcome({
      status: typeof body.status === 'string' ? body.status : 'erased',
      blockedBecause: Array.isArray(body.blockedBecause)
        ? body.blockedBecause.filter((line): line is string => typeof line === 'string')
        : [],
    })
    onErased?.()
  })

  const confirm = useCallback(async () => {
    const write = (await enqueue({
      method: 'POST',
      path: `/customers/${encodeURIComponent(customerId)}/erasure`,
      label: t('erasure.queueLabel', { name: customerName }),
      body: {
        // `RET-01`, and the server refuses without it. The plan above is what was acknowledged.
        acknowledgedRetention: true,
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [customerId, customerName, enqueue, t])

  if (!canErase) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.erasure'))

  const displayDay = (value: string): string => formatDay(value, timezone, locale)

  if (outcome !== null) {
    return (
      <section className="lm-erasure" aria-labelledby="erasure-outcome">
        <h2 id="erasure-outcome" className="lm-erasure__heading">
          {t(outcome.status === 'queued' ? 'erasure.queuedHeading' : 'erasure.doneHeading')}
        </h2>
        {outcome.status === 'queued' ? (
          <>
            <p className="lm-erasure__effects">{t('erasure.queued')}</p>
            <ul className="lm-erasure__list">
              {outcome.blockedBecause.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </>
        ) : (
          <p className="lm-erasure__effects">{t('erasure.done')}</p>
        )}
      </section>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        className="lm-button lm-button--secondary"
        onClick={() => {
          setOpen(true)
        }}
      >
        {t('erasure.start')}
      </button>
    )
  }

  return (
    <section className="lm-erasure" aria-labelledby="erasure-heading">
      <h2 id="erasure-heading" className="lm-erasure__heading">
        {t('erasure.heading')}
      </h2>
      <p className="lm-erasure__effects">{t('erasure.whatItIs')}</p>

      {/*
        A failed plan is an error and never a ready: the confirm button lives only in the ready
        branch, so a plan that could not be read cannot be acknowledged. Cancel is outside the
        surface so it is still reachable from the error.
      */}
      <DataSurface
        state={
          loadError !== null
            ? {
                kind: 'error',
                message: loadError,
                onRetry: () => {
                  setLoadError(null)
                  setAnswer(null)
                  setAttempt((n) => n + 1)
                },
              }
            : answer === null
              ? { kind: 'loading' }
              : { kind: 'ready' }
        }
        shape="lines"
        rows={4}
        what={t('erasure.what')}
      >
        {answer === null ? null : answer.alreadyErasedAt !== null ? (
          <p className="lm-erasure__effects">{t('erasure.alreadyDone')}</p>
        ) : (
          <>
            {answer.queuedSince === null ? null : (
              // The date the customer asked, which a queued request keeps across attempts.
              <p className="lm-hint">
                {t('erasure.onFileSince', { date: displayDay(answer.queuedSince) })}
              </p>
            )}

            <h3 className="lm-erasure__subheading">{t('erasure.goesHeading')}</h3>
            <ul className="lm-erasure__list">
              {answer.plan.toErase.map((item) => (
                <li key={item.id}>{item.label}</li>
              ))}
            </ul>

            <h3 className="lm-erasure__subheading">{t('erasure.staysHeading')}</h3>
            {answer.plan.toRetain.length === 0 ? (
              <p className="lm-erasure__effects">{t('erasure.nothingStays')}</p>
            ) : (
              <>
                <ul className="lm-erasure__list">
                  {answer.plan.toRetain.map((item) => (
                    <li key={item.id}>{item.label}</li>
                  ))}
                </ul>
                {/* `RET-01`'s "and why", one line per law, in the words the customer is read. */}
                <ul className="lm-erasure__notice">
                  {answer.plan.retentionNotice.map((notice) => (
                    <li key={notice}>{notice}</li>
                  ))}
                </ul>
              </>
            )}

            {answer.blockedBecause.length === 0 ? null : (
              <>
                <h3 className="lm-erasure__subheading">{t('erasure.blockedHeading')}</h3>
                <ul className="lm-erasure__list">
                  {answer.blockedBecause.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
                <p className="lm-erasure__effects">{t('erasure.blockedMeans')}</p>
              </>
            )}

            {line === null ? null : (
              <p className="lm-queue-state" role="status">
                {line}
              </p>
            )}

            <button
              type="button"
              className="lm-button lm-button--danger"
              disabled={writeId !== null}
              onClick={() => {
                void confirm()
              }}
            >
              {t(
                answer.blockedBecause.length === 0
                  ? 'erasure.confirm'
                  : 'erasure.confirmWhileBlocked',
              )}
            </button>
          </>
        )}
      </DataSurface>

      {/* The way out. Opening this panel is how somebody reads what an erasure would do, and
              until this button existed the only thing to press afterwards was the erasure. It is
              disabled once a write is in flight: closing then would hide the outcome, which is the
              only place the shop is told what was removed. */}
      <button
        type="button"
        className="lm-button lm-button--secondary"
        disabled={writeId !== null}
        onClick={() => {
          setOpen(false)
          setAnswer(null)
          setLoadError(null)
        }}
      >
        {t('erasure.cancel')}
      </button>
    </section>
  )
}
